import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight } from 'react-icons/fi';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectAuthError, selectAuthLoading, selectIsAuthenticated } from '../../store/selectors';
import { clearError, loginUser } from '../../store/slices/authSlice';
import { REMEMBER_ME_STORAGE_KEY, supabase } from '../../utils/supabaseClient';

const REMEMBERED_EMAIL_STORAGE_KEY = 'auth.rememberedEmail';
// import { useAuth } from '../../hooks/useAuth';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // const { login } = useAuth();

  const [email, setEmail] = useState(() => {
    if (typeof window === 'undefined') {
      return '';
    }

    try {
      const storedPreference = window.localStorage.getItem(REMEMBER_ME_STORAGE_KEY);
      if (storedPreference !== 'true') {
        return '';
      }

      return window.localStorage.getItem(REMEMBERED_EMAIL_STORAGE_KEY) ?? '';
    } catch (error) {
      console.warn('Unable to load remembered email:', error);
      return '';
    }
  });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return window.localStorage.getItem(REMEMBER_ME_STORAGE_KEY) === 'true';
    } catch (error) {
      console.warn('Unable to load remember me preference:', error);
      return false;
    }
  });
  // const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch()
  const isLoading = useAppSelector(selectAuthLoading)
  const error = useAppSelector(selectAuthError)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  // const [error, setError] = useState<string | null>(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4 }
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    // setError(null);
    // setIsLoading(true);

    // try {
    //   await login(email, password, rememberMe);
    //   navigate('/dashboard');
    // } catch (err) {
    //   setError(t('auth.invalidCredentials'));
    // } finally {
    //   setIsLoading(false);
    // }

    dispatch(loginUser({ email, password, rememberMe }))
      .unwrap()
      .then(() => {
        if (typeof window !== 'undefined') {
          try {
            if (rememberMe) {
              window.localStorage.setItem(REMEMBERED_EMAIL_STORAGE_KEY, email);
            } else {
              window.localStorage.removeItem(REMEMBERED_EMAIL_STORAGE_KEY);
            }
          } catch (error) {
            console.warn('Unable to persist remembered email:', error);
          }
        }

        navigate('/dashboard');
      })
      .catch((error) => {
        // console.log(error.message);
      });
  };

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (!rememberMe || typeof window === 'undefined') {
      return;
    }

    try {
      if (email) {
        window.localStorage.setItem(REMEMBERED_EMAIL_STORAGE_KEY, email);
      } else {
        window.localStorage.removeItem(REMEMBERED_EMAIL_STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Unable to sync remembered email value:', error);
    }
  }, [email, rememberMe]);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // If already authenticated, navigate to dashboard immediately
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleRememberMeToggle = () => {
    setRememberMe((previous) => {
      const nextValue = !previous;

      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(REMEMBER_ME_STORAGE_KEY, nextValue ? 'true' : 'false');

          if (nextValue) {
            if (email) {
              window.localStorage.setItem(REMEMBERED_EMAIL_STORAGE_KEY, email);
            }
          } else {
            window.localStorage.removeItem(REMEMBERED_EMAIL_STORAGE_KEY);
          }
        } catch (error) {
          console.warn('Unable to update remember me preference:', error);
        }
      }

      return nextValue;
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Subtle background decorative elements */}
      <div className="absolute inset-0 overflow-hidden opacity-40">
        <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-gradient-to-tr from-secondary/15 to-primary/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full blur-3xl"></div>
      </div>
      {/* Header with Language Switcher */}


      <div className="flex-1 flex items-center justify-center px-4 py-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full max-w-md bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50 dark:border-slate-700/50 p-8 relative overflow-hidden"
        >
          {/* Elegant top accent */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-secondary"></div>

          {/* Modern login form */}
          <motion.div variants={itemVariants} className="relative z-10">
            {/* Welcome section */}
            <div className="text-center mb-8">
              <div className='flex justify-center '>
                <Link to="/" className="flex items-center flex-col space-x-3 group">
                  <motion.img src="images/logo/Dineai.png" alt="Logo" className="w-12 h-12" />
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-primary drop-shadow-sm group-hover:scale-105 transition-transform duration-300"
                  >
                    Dine.ai
                  </motion.div>

                </Link>
              </div>

              <p className="text-gray-600 dark:text-gray-300 text-sm pt-3">
                {t('auth.signin')}
              </p>
            </div>



            <form onSubmit={handleLogin}>
              <motion.div variants={itemVariants} className="space-y-5">
                {/* Email field */}
                <div className="space-y-2">
                  <InputField
                    type="email"
                    label={t('auth.email')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    icon={<FiMail className="text-primary" />}
                    required
                    autoComplete="username"
                    name="username"
                    id="username"
                    className="bg-gray-50/80 dark:bg-slate-700/80 border-gray-200/50 dark:border-orange-600/50 focus:border-orange-600/50 focus:ring-primary/20 rounded-xl"
                  />
                </div>

                {/* Password field */}
                <div className="space-y-2">
                  <InputField
                    type={showPassword ? 'text' : 'password'}
                    label={t('auth.password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.passwordPlaceholder')}
                    icon={<FiLock className="text-primary" />}
                    required
                    autoComplete="current-password"
                    name="password"
                    id="password"
                    className="bg-gray-50/80 dark:bg-slate-700/80 border-gray-200/50 dark:border-orange-600/50 focus:border-orange-600/50 focus:ring-primary/20 rounded-xl"
                    rightElement={
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="text-gray-500 hover:text-primary dark:text-gray-400 dark:hover:text-primary transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-lg p-2"
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    }
                  />
                </div>                {/* Options row */}
                <div className="flex items-center justify-between pt-2">
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-primary border-gray-300 dark:border-slate-600 rounded focus:ring-primary focus:ring-2 transition-colors"
                      checked={rememberMe}
                      onChange={handleRememberMeToggle}
                    />
                    <span className="ms-3 text-sm text-gray-600 dark:text-gray-300 group-hover:text-primary transition-colors">
                      {t('auth.rememberMe')}
                    </span>
                  </label>

                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary hover:text-primary-dark font-medium transition-colors duration-200 hover:underline"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>

                {/* Login button */}
                <motion.div variants={itemVariants} className="pt-4">
                  <Button
                    type="submit"
                    fullWidth
                    size="lg"
                    isLoading={isLoading}
                    rightIcon={<FiArrowRight />}
                    className="bg-gradient-to-r from-primary to-secondary hover:from-primary-dark hover:to-secondary text-white font-semibold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {t('auth.login')}
                  </Button>
                </motion.div>
              </motion.div>
            </form>


          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}; export default Login;